import { ChangeHistory } from '@mui/icons-material';
import {
    Box,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    OutlinedInput,
    SvgIcon,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import React, { useCallback, useContext } from 'react';
import { ReactComponent as TriangleIcon } from 'assets/images/Triangle.svg';
import { checkboxStatus } from 'modules/home/manage-packets/listing-packets/components/data/checkboxStatus';
import { DashboardMeta } from 'modules/authentication/components/DashboardPage';

import { useSelector } from 'react-redux';

const headCells: readonly HeadCell[] = [
    {
        id: 'packetStatus',
        label: 'Cross Check Status',
    },

    {
        id: 'uploadedDate',
        label: 'Uploaded',
    },
    {
        id: 'SLADate',
        label: 'SLA',
    },
    {
        id: 'clientName',
        label: 'Checklists Name',
    },
    {
        id: 'binNumber',
        label: 'Reference Number',
    },
    {
        id: 'loanNumber',
        label: 'Envelope Number',
    },
    {
        id: 'operatorId',
        label: 'Member ID',
    },
    {
        id: 'packetType',
        label: 'Cross Check Type',
    },
    {
        id: 'completedDate',
        label: 'Completed',
    },
    {
        id: 'escalatedDate',
        label: 'Escalated',
    },
];

interface EnhancedTableProps {
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
        search: string,
        ordered?: 'asc' | 'desc'
    ) => void;
    order: Order;
    orderBy: any;
    formikRef: any;
    updateSearch: any;
    searchValues: any;
    setSearchValues: any;
}

/**
 * EnhancedTable component.
 *
 * @returns {React.ReactElement}
 */
const EnhancedTableHead = (props: EnhancedTableProps): React.ReactElement => {
    const {
        order,
        orderBy,
        onRequestSort,
        formikRef,
        updateSearch,
        searchValues,
        setSearchValues,
    } = props;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const role = useSelector((state: any) => state.authState.role);
    const formRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorTC, setAnchorTC] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [isEnabled, setEnabled] = React.useState<boolean>(false);
    const DashboardMetaProps = useContext(DashboardMeta);
    const [selected, setSelected] = React.useState<keyof Data>('binNumber');
    const [focusedField, setFocusedField] = React.useState<string | null>(null);

    const actionOnOutlinedInput =
        (property: keyof Data) => (event: React.MouseEvent<HTMLElement>) => {
            if (
                property !== 'binNumber' &&
                property !== 'clientName' &&
                property !== 'loanNumber' &&
                property !== 'operatorId'
            ) {
                setAnchorEl(event.currentTarget);
                setEnabled(false);
            } else {
                setEnabled(true);
            }
            setSelected(property);
        };

    const actionOnFilteringCloseMenu = React.useCallback(() => {
        setAnchorEl(null);
        setEnabled(false);
        if (inputRef.current !== null) {
            inputRef.current.value = '';
        }
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const actionOnFilteringCloseInput = React.useCallback(() => {
        if (
            anchorTC === null ||
            (anchorTC && anchorTC.classList.contains(selected) === false)
        ) {
            setAnchorEl(null);
            setEnabled(false);
            if (inputRef.current !== null) {
                inputRef.current.value = '';
            }
        }
    }, [anchorTC, selected]);

    const actionOnMouseOverTableCell = React.useCallback(
        (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
            setAnchorTC(event.currentTarget);
        },
        []
    );
    const actionOnMouseLeaveTableCell = React.useCallback(() => {
        setAnchorTC(null);
    }, []);

    const actionOnClickSort =
        (property: keyof Data, _search: any, ordered?: 'asc' | 'desc') =>
        (event: React.MouseEvent<any>) => {
            setAnchorEl(null);

            const resetSearchValues: Record<string, string> = {
                [property]: _search,
            };

            if (property === 'packetStatus' || property === 'packetType') {
                setSearchValues(resetSearchValues);
            } else {
                const idsToClear = [
                    'uploadedDate',
                    'SLADate',
                    'completedDate',
                    'escalatedDate',
                ];

                idsToClear.forEach((id) => {
                    if (id !== property) {
                        resetSearchValues[id] = '';
                    }
                });
                setSearchValues(resetSearchValues);
            }

            onRequestSort(event, property, _search, ordered);
        };

    const getSortDirection = (
        _orderBy: string,
        _order: Order,
        idCell: keyof Data
    ): 'asc' | 'desc' => {
        if (_orderBy === idCell) {
            if (_order === 'asc') {
                return 'asc';
            }
            return 'desc';
        }
        return 'desc';
    };

    const setSearchBy = (
        event: any,
        property: any,
        _search: any,
        ordered?: 'asc' | 'desc'
    ): void => {
        onRequestSort(event, property, _search, ordered);
    };

    const onChangeField = (field: string, ev: string): void => {
        if (formikRef.current) {
            if (field === 'clientName') {
                formikRef.current.setFieldValue('search_client', ev);
            } else if (field === 'operatorId') {
                formikRef.current.setFieldValue('search_operator', ev);
            } else if (field === 'binNumber') {
                formikRef.current.setFieldValue('search_bin', ev);
            } else if (field === 'loanNumber') {
                formikRef.current.setFieldValue('search_loan', ev);
            }
        }
    };

    const getTitle = (id: any): any => {
        if (
            ['binNumber', 'clientName', 'loanNumber', 'operatorId'].includes(id)
        ) {
            return id;
        }
        return (
            id
                ?.replace(/([A-Z])/g, ' $1')
                .replace(/^./, (s: string) => s.toUpperCase()) || ''
        );
    };

    const formatSearchValue = (value: string): any =>
        value
            ? value
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (s) => s.toUpperCase())
            : '';
    const parseSeachValuesForHeadTablePlaceholder = useCallback(
        (tableHeadId: string) => {
            if (
                [
                    'binNumber',
                    'clientName',
                    'loanNumber',
                    'operatorId',
                ].includes(tableHeadId)
            ) {
                return searchValues[tableHeadId];
            }
            if (['packetStatus'].includes(tableHeadId)) {
                const keyStatus = searchValues[tableHeadId];
                const labelStatus = checkboxStatus.find(
                    (el) =>
                        el.value.toUpperCase() ===
                        String(keyStatus).toUpperCase()
                );

                if (labelStatus) {
                    return labelStatus.label;
                }
                return (
                    searchValues[tableHeadId]
                        ?.replace(/([A-Z])/g, ' $1')
                        .replace(/^./, (s: string) => s.toUpperCase()) ?? ''
                );
            }
            return (
                searchValues[tableHeadId]
                    ?.replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (s: string) => s.toUpperCase()) ?? ''
            );
        },
        [searchValues]
    );
    return (
        <TableHead className="default-table-head">
            <TableRow>
                {headCells
                    .filter(
                        (e) =>
                            (role === 'operator' && e.id !== 'escalatedDate') ||
                            role !== 'operator'
                    )
                    .map((headCell, index) => {
                        const isEnabledSelected =
                            isEnabled && selected === headCell.id;
                        const isClassEnabled = isEnabledSelected
                            ? 'enabled'
                            : 'disabled';
                        const isTypeToFilter = !!(
                            headCell.id === 'operatorId' ||
                            headCell.id === 'loanNumber' ||
                            headCell.id === 'binNumber' ||
                            headCell.id === 'clientName'
                        );
                        const disabledFilter =
                            headCell.id === 'escalatedDate' ||
                            headCell.id === 'completedDate';
                        const conditionalPlaceholder = disabledFilter
                            ? ''
                            : 'Type to Filter';
                        const placeholder =
                            isTypeToFilter || disabledFilter
                                ? conditionalPlaceholder
                                : 'Click to Filter';
                        const title = getTitle(headCell.id);

                        const valueToSort = formatSearchValue(
                            [
                                'binNumber',
                                'clientName',
                                'loanNumber',
                                'operatorId',
                            ].includes(headCell.id)
                                ? searchValues[headCell.id]
                                : searchValues[headCell.id]
                        );

                        return (
                            <TableCell
                                key={`${headCell.id} - ${index}`}
                                align="left"
                                className={headCell.id}
                                sortDirection={getSortDirection(
                                    orderBy,
                                    order,
                                    headCell.id
                                )}
                                onMouseLeave={actionOnMouseLeaveTableCell}
                                onMouseEnter={actionOnMouseOverTableCell}
                            >
                                <Box>{headCell.label}</Box>
                                <Box
                                    ref={formRef}
                                    className={`table-filter-with-input ${isClassEnabled}`}
                                >
                                    {disabledFilter === false ? (
                                        <OutlinedInput
                                            className="outlined-table-filter"
                                            onFocus={() => {
                                                if (
                                                    [
                                                        'binNumber',
                                                        'clientName',
                                                        'loanNumber',
                                                        'operatorId',
                                                    ].includes(title)
                                                ) {
                                                    updateSearch(title);
                                                }

                                                if (
                                                    focusedField !==
                                                        headCell.id ||
                                                    (focusedField ===
                                                        headCell.id &&
                                                        searchValues[
                                                            focusedField
                                                        ] === '')
                                                ) {
                                                    if (
                                                        [
                                                            'binNumber',
                                                            'clientName',
                                                            'loanNumber',
                                                            'operatorId',
                                                        ].includes(title) ===
                                                            true &&
                                                        [
                                                            'binNumber',
                                                            'clientName',
                                                            'loanNumber',
                                                            'operatorId',
                                                        ].includes(
                                                            focusedField ?? ''
                                                        ) === true
                                                    ) {
                                                        // setSearchValues({
                                                        //     binNumber: '',
                                                        //     clientName: '',
                                                        //     loanNumber: '',
                                                        //     operatorId: '',
                                                        // });
                                                        if (formikRef.current) {
                                                            // formikRef.current.setFieldValue(
                                                            //     'search_loan',
                                                            //     ''
                                                            // );
                                                            // formikRef.current.setFieldValue(
                                                            //     'search_bin',
                                                            //     ''
                                                            // );
                                                            // formikRef.current.setFieldValue(
                                                            //     'search_client',
                                                            //     ''
                                                            // );
                                                            // formikRef.current.setFieldValue(
                                                            //     'search_operator',
                                                            //     ''
                                                            // );
                                                        }
                                                    }
                                                    setFocusedField(
                                                        headCell.id
                                                    );
                                                }
                                                DashboardMetaProps?.setTableHeadingContentFocus(
                                                    true
                                                );
                                            }}
                                            inputRef={inputRef}
                                            onChange={(e) => {
                                                setSearchValues({
                                                    [headCell.id]:
                                                        e.target.value,
                                                });
                                                onChangeField(
                                                    headCell.id,
                                                    e.target.value
                                                );
                                            }}
                                            notched={false}
                                            id={`outlined-adornment-${headCell.id}`}
                                            type="text"
                                            inputProps={{
                                                className: headCell.id,
                                            }}
                                            disabled={disabledFilter}
                                            value={parseSeachValuesForHeadTablePlaceholder(
                                                headCell.id
                                            )}
                                            readOnly={!isTypeToFilter}
                                            onClick={actionOnOutlinedInput(
                                                headCell.id
                                            )}
                                            onKeyDown={(event: any) => {
                                                if (event.key === 'Enter') {
                                                    actionOnOutlinedInput(
                                                        headCell.id
                                                    )(event);
                                                }
                                            }}
                                            aria-controls={
                                                open
                                                    ? `demo-positioned-menu${index}`
                                                    : undefined
                                            }
                                            aria-haspopup="true"
                                            aria-expanded={
                                                open ? 'true' : undefined
                                            }
                                            onBlur={() => {
                                                if (
                                                    focusedField ===
                                                        headCell.id &&
                                                    !searchValues.binNumber &&
                                                    !searchValues.clientName &&
                                                    !searchValues.loanNumber &&
                                                    !searchValues.operatorId
                                                ) {
                                                    setSearchValues({
                                                        binNumber: '',
                                                        clientName: '',
                                                        loanNumber: '',
                                                        operatorId: '',
                                                    });
                                                    updateSearch('');
                                                    setFocusedField(null);
                                                }

                                                DashboardMetaProps?.setTableHeadingContentFocus(
                                                    false
                                                );
                                                if (
                                                    focusedField &&
                                                    [
                                                        'clientName',
                                                        'binNumber',
                                                        'loanNumber',
                                                        'operatorId',
                                                    ].includes(focusedField)
                                                ) {
                                                    actionOnFilteringCloseInput();
                                                }
                                            }}
                                            endAdornment={
                                                isTypeToFilter ||
                                                disabledFilter ? (
                                                    ''
                                                ) : (
                                                    <InputAdornment
                                                        position="end"
                                                        className={
                                                            !isEnabledSelected
                                                                ? 'pointer-none'
                                                                : ''
                                                        }
                                                    >
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            disabled={
                                                                !isEnabledSelected
                                                            }
                                                            edge="end"
                                                            onClick={(
                                                                ev: any
                                                            ) => {
                                                                setSearchBy(
                                                                    ev,
                                                                    title,
                                                                    valueToSort
                                                                );
                                                            }}
                                                        >
                                                            {isEnabledSelected ? (
                                                                <ChangeHistory className="iconButtonImage" />
                                                            ) : (
                                                                <SvgIcon
                                                                    component={
                                                                        TriangleIcon
                                                                    }
                                                                    inheritViewBox
                                                                    className="iconButtonImage"
                                                                    fontSize="inherit"
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }
                                            placeholder={placeholder}
                                            onKeyPress={(ev) => {
                                                if (ev.key === 'Enter') {
                                                    setSearchBy(
                                                        ev,
                                                        title,
                                                        valueToSort
                                                    );
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Box className="outlined-table-filter filled-layout" />
                                    )}
                                </Box>
                            </TableCell>
                        );
                    })}
            </TableRow>
            <Menu
                data-testid="menu-dashboard-dropdown"
                action={(el) => el?.updatePosition()}
                ref={menuRef}
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                classes={{
                    paper: 'menu-dashboard-paper',
                    list: 'menu-dashboard-list',
                }}
                onClose={actionOnFilteringCloseMenu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {/* Packet Status */}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetStatus', '')}
                    >
                        View All
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetStatus', 'completed')}
                    >
                        Completed
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetStatus', 'verified')}
                    >
                        Verified
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetStatus', 'verifying')}
                    >
                        Processing QA
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort(
                            'packetStatus',
                            'categorisationFailed'
                        )}
                    >
                        Categorisation Failed
                    </MenuItem>
                )}
                {role !== 'operator' && selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetStatus', 'escalated')}
                    >
                        Escalated
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort(
                            'packetStatus',
                            'reviewRequired'
                        )}
                    >
                        Review Required
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort(
                            'packetStatus',
                            'sentForRework'
                        )}
                    >
                        Sent for rework
                    </MenuItem>
                )}
                {selected === 'packetStatus' && (
                    <MenuItem
                        className={
                            selected === 'packetStatus' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort(
                            'packetStatus',
                            'openByAnotherOP'
                        )}
                    >
                        Open By another Operator
                    </MenuItem>
                )}

                {/* Packet Type */}
                {selected === 'packetType' && (
                    <MenuItem
                        className={
                            selected === 'packetType' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetType', '')}
                    >
                        View All
                    </MenuItem>
                )}
                {selected === 'packetType' && (
                    <MenuItem
                        className={
                            selected === 'packetType' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetType', 'digital')}
                    >
                        Digital
                    </MenuItem>
                )}
                {selected === 'packetType' && (
                    <MenuItem
                        className={
                            selected === 'packetType' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetType', 'hybrid')}
                    >
                        Hybrid
                    </MenuItem>
                )}
                {selected === 'packetType' && (
                    <MenuItem
                        className={
                            selected === 'packetType' ? 'show' : 'hidden'
                        }
                        onClick={actionOnClickSort('packetType', 'incomplete')}
                    >
                        Incomplete
                    </MenuItem>
                )}

                {/* Sorting */}
                {selected !== 'packetStatus' && selected !== 'packetType' && (
                    <MenuItem
                        className="show"
                        onClick={actionOnClickSort(selected, 'Newest First')}
                    >
                        Newest First
                    </MenuItem>
                )}
                {selected !== 'packetStatus' && selected !== 'packetType' && (
                    <MenuItem
                        className="show"
                        onClick={actionOnClickSort(selected, 'Oldest First')}
                    >
                        Oldest First
                    </MenuItem>
                )}
            </Menu>
        </TableHead>
    );
};

export default EnhancedTableHead;
